import React, { useState } from 'react';
import './Sidebar.scss';
import { NavLink, Link } from 'react-router-dom';
import Logo from './components/shared/Logo.jsx';

function Sidebar({ isAdmin, isDeveloper = false, isLoggedIn }) {
  const [openSections, setOpenSections] = useState({
    global: true,
    regional: true,
  });

  const toggleSection = (section) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const dashboardLinks = {
    global: {
      title: 'Global Outlooks',
      icon: 'globe',
      links: [
        { to: '/g/global-crude/outlook', label: 'Crude', toolTip: 'Crude Oil' },
        { to: '/g/global-fuels/outlook', label: 'Fuels', toolTip: 'Fuels' },
        { to: '/g/global-refining/outlook', label: 'Refining', toolTip: 'Refining' },
        { to: '/g/global-petrochemical-feedstocks/outlook', label: 'Petrochemical Feedstocks', toolTip: 'Petrochemical Feedstocks' },
        { to: '/g/global-renewable-fuels/outlook', label: 'Renewable Fuels', toolTip: 'Alternative Fuels' },
      ],
    },
    regional: {
      title: 'Regional Watches',
      icon: 'map-trifold',
      links: [
        { to: '/r/regional-north-america/outlook', label: 'North America', toolTip: 'North America' },
        { to: '/r/regional-latin-america/outlook', label: 'Latin America', toolTip: 'Latin America' },
        { to: '/r/regional-europe/outlook', label: 'Europe', toolTip: 'Europe' },
        { to: '/r/regional-middle-east/outlook', label: 'Middle East', toolTip: 'Middle East' },
        { to: '/r/regional-africa/outlook', label: 'Africa', toolTip: 'Africa' },
        { to: '/r/regional-asia/outlook', label: 'Asia', toolTip: 'Asia' },
        { to: '/r/regional-china/outlook', label: 'China', toolTip: 'China' },
        { to: '/r/regional-cis/outlook', label: 'C.I.S.', toolTip: 'C.I.S.' }
      ],
    },
  };

  // Sidebar bottom links data
  const sidebarBottomLinks = [
    {
      to: '/u/profile',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="7" r="4" stroke="#fff" strokeWidth="1.5" />
          <path d="M3 17c0-2.5 3-4 7-4s7 1.5 7 4" stroke="#fff" strokeWidth="1.5" />
        </svg>
      ),
      title: 'Profile',
      toolbar: 'User',
      isLoggedIn: true,
      isAdmin: false,
      menuOrder: 4
    },
    {
      to: '/u/admin',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M10 2l7 3v5c0 5-3.5 8-7 8s-7-3-7-8V5l7-3z" stroke="#fff" strokeWidth="1.5" fill="none" />
          <circle cx="15" cy="5" r="2" fill="#0078d4" />
        </svg>
      ),
      title: 'Admin',
      toolbar: 'Admin',
      isLoggedIn: true,
      showFor: (isAdmin || isDeveloper),
      menuOrder: 2
    },
  ];

  return (
    <div className="sidebar">
      <div className="sidebar-logo">
        <Link to="/" className="sidebar-logo-link">
          <Logo />
        </Link>
      </div>
      <div className="sidebar-top">
        <NavLink
          to="/recent-publications"
          className={({ isActive }) => `sidebar-link sidebar-top-link${isActive ? ' active' : ''}`}
          title="Publications"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: 6, flexShrink: 0 }}>
            <rect x="2" y="1" width="12" height="14" rx="1.5" stroke="#fff" strokeWidth="1.3" />
            <line x1="5" y1="5" x2="11" y2="5" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
            <line x1="5" y1="8" x2="11" y2="8" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
            <line x1="5" y1="11" x2="9" y2="11" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
          Publications
        </NavLink>

        {Object.entries(dashboardLinks).map(([sectionKey, section]) => {
          // Check if section is restricted to admin
          if (section.isAdmin && !isAdmin) return null;
          return (
            <div key={sectionKey} className="sidebar-section">
              <h2
                className="sidebar-section-header"
                onClick={() => toggleSection(sectionKey)}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '4px', padding: '0 12px' }}
              >
                <span className={`caret ${openSections[sectionKey] ? 'open' : ''}`}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="section-icon">
                  <img
                    src={`/static/icons/${section.icon}.svg`}
                    alt={section.title}
                    width="16"
                    height="16"
                  />
                </span>
                {section.title}
              </h2>
              {openSections[sectionKey] && (
                <ul className="dataset-list">
                  {section.links
                    .filter(link => !(link.isAdmin && !isAdmin)) // Filter admin-only links
                    .map((link) => (
                      <li key={link.to || link.label}>
                        <NavLink
                          to={link.to}
                          className={({ isActive }) => (isActive ? 'sidebar-link active' : 'sidebar-link')}
                          title={link.toolTip || link.label}
                        >
                          {link.label}
                        </NavLink>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      <div className="sidebar-bottom">
        <div className="sidebar-section">
          {sidebarBottomLinks
            .filter(link => {
              if (typeof link.showFor === 'boolean' && !link.showFor) return false;
              if (link.isAdmin && !isAdmin) return false;
              if (link.isLoggedIn && !isLoggedIn) return false;
              return true;
            })
            .sort((a, b) => (a.menuOrder ?? 0) - (b.menuOrder ?? 0))
            .map(link => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) => (isActive ? 'sidebar-link active' : 'sidebar-link')}
                title={link.toolbar}
              >
                <span className="sidebar-icon" role="img" aria-label={link.toolbar} style={{ marginRight: '4px' }}>
                  {link.icon}
                </span>
                <span>{link.title}</span>
              </NavLink>
            ))}
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
